import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Inbox, Trash2, Loader2, Users, Sparkles, UserCircle2, Bookmark, Star, Crown, Eye, TrendingUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AdminAccounts } from "@/components/AdminAccounts";

const EXPANDABLE = { users: "users", premium: "premium", paywall: "paywall", conversions: "conversions" };

export default function Admin() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/feedback").then((res) => setItems(res.data)).catch(() => setItems([]));
      api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => setStats(null));
      api.get("/admin/reviews").then((res) => setReviews(res.data)).catch(() => setReviews([]));
    }
  }, [user]);

  if (loading) return <div className="text-center pt-32 font-decree text-xl text-[#7A756E]">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const remove = async (id) => {
    await api.delete(`/admin/feedback/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast.success("Feedback removed");
  };

  const removeReview = async (id) => {
    if (!window.confirm("Remove this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    setReviews((prev) => prev.filter((i) => i.id !== id));
    toast.success("Review removed");
  };

  const toggleFeature = async (r) => {
    const { data } = await api.patch(`/admin/reviews/${r.id}/feature`, { featured: !r.featured });
    setReviews((prev) => prev.map((i) => (i.id === r.id ? { ...i, featured: data.featured } : i)));
    toast.success(data.featured ? "Now showing on the landing page" : "Hidden from the landing page");
  };

  const statCards = stats ? [
    { key: "users", label: "User accounts", value: stats.total_users, sub: `${stats.google_users} Google · ${stats.email_users} email`, Icon: Users },
    { key: "gen-total", label: "Decrees generated", value: stats.generations_total, sub: "all-time", Icon: Sparkles },
    { key: "gen-guest", label: "Guest usage", value: stats.generations_guest, sub: `${stats.generations_user} by members`, Icon: UserCircle2 },
    { key: "saved", label: "Saved decree sets", value: stats.saved_sessions, sub: `${stats.feedback_count} feedback`, Icon: Bookmark },
    { key: "premium", label: "Premium subscribers", value: stats.premium_users ?? 0, sub: `${stats.trialing_users ?? 0} trialing · ${stats.free_users ?? 0} free`, Icon: Crown },
    { key: "paywall", label: "Paywall views", value: stats.paywall_views ?? 0, sub: `${stats.trial_starts ?? 0} trials started`, Icon: Eye },
    { key: "conversions", label: "Conversions", value: stats.conversions ?? 0, sub: `${stats.cancellations ?? 0} cancellations`, Icon: TrendingUp },
  ] : [];

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 pt-12 pb-24" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-2">
        <Inbox className="w-6 h-6 text-[#2C2A29]" strokeWidth={1.5} />
        <p className="font-body text-sm uppercase tracking-[0.2em] text-[#7A756E]">Admin · Dashboard</p>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight">Usage & feedback</h1>
      <p className="font-decree text-lg text-[#7A756E] mt-3">App adoption at a glance, and every submission from guests and subscribers.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="admin-stats">
        {stats === null ? (
          <div className="col-span-full py-8 text-center"><Loader2 className="w-6 h-6 text-[#2C2A29] animate-spin mx-auto" /></div>
        ) : (
          statCards.map(({ key, label, value, sub, Icon }) => {
            const group = EXPANDABLE[key];
            const open = group && openGroup === group;
            const Tag = group ? "button" : "div";
            return (
              <Tag key={key} data-testid={`stat-${key}`}
                onClick={group ? () => setOpenGroup(open ? null : group) : undefined}
                aria-expanded={group ? open : undefined}
                className={`relative text-left border rounded-sm p-5 transition-colors duration-300 ${open ? "border-[#9B7E4E] bg-[#F3F0EA]" : "border-[#D9D4CC] bg-[#FDFBF7]"} ${group ? "hover:border-[#9B7E4E] cursor-pointer" : ""}`}>
                <Icon className="w-5 h-5 text-[#9B7E4E]" strokeWidth={1.5} />
                <p className="font-heading text-3xl text-[#2C2A29] mt-3" data-testid={`stat-value-${key}`}>{value}</p>
                <p className="font-body text-sm text-[#4A4744] mt-1">{label}</p>
                <p className="font-body text-xs text-[#7A756E] mt-0.5">{sub}</p>
                {group && (
                  <span className="absolute top-4 right-4 flex items-center gap-1 font-body text-[10px] uppercase tracking-wide text-[#9B7E4E]">
                    {open ? "Hide" : "View"} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  </span>
                )}
              </Tag>
            );
          })
        )}
      </div>
      {openGroup && <AdminAccounts group={openGroup} onClose={() => setOpenGroup(null)} />}

      <h2 className="font-heading text-2xl text-[#2C2A29] tracking-tight mt-16 flex items-center gap-2.5">
        <Star className="w-6 h-6 text-[#9B7E4E]" strokeWidth={1.5} /> Reviews
      </h2>
      <p className="font-body text-sm text-[#7A756E] mt-2">Only reviews you mark “Show on landing page” appear publicly.</p>
      {reviews === null ? (
        <div className="py-12 text-center"><Loader2 className="w-7 h-7 text-[#2C2A29] animate-spin mx-auto" /></div>
      ) : reviews.length === 0 ? (
        <p className="font-decree text-xl text-[#4A4744] mt-6" data-testid="reviews-empty">No reviews yet.</p>
      ) : (
        <ul className="mt-6 space-y-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`review-row-${r.id}`} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="w-4 h-4" strokeWidth={1.5}
                        fill={n <= r.rating ? "#9B7E4E" : "none"} color={n <= r.rating ? "#9B7E4E" : "#D9D4CC"} />
                    ))}
                    <span className="ml-2 font-body text-sm text-[#7A756E]">{r.name || "Anonymous"}</span>
                  </div>
                  {r.comment && <p className="font-body text-base text-[#2C2A29] mt-3 whitespace-pre-wrap">{r.comment}</p>}
                  <p className="font-body text-sm text-[#7A756E] mt-3">{new Date(r.created_at).toLocaleString()}</p>
                  {r.comment && (
                    <button data-testid={`feature-review-${r.id}`} onClick={() => toggleFeature(r)} aria-pressed={!!r.featured}
                      className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors duration-300 ${r.featured ? "bg-[#9B7E4E] text-[#FAF9F6] border-[#9B7E4E]" : "border-[#D9D4CC] text-[#4A4744] hover:bg-[#EAE6DF]"}`}>
                      <Eye className="w-3.5 h-3.5" /> {r.featured ? "Showing on landing page" : "Show on landing page"}
                    </button>
                  )}
                </div>
                <button data-testid={`delete-review-${r.id}`} onClick={() => removeReview(r.id)}
                  aria-label="Delete review" className="shrink-0 text-[#B7AE9F] hover:text-[#9B3B3B] transition-colors duration-300 p-2">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <h2 className="font-heading text-2xl text-[#2C2A29] tracking-tight mt-16">User feedback</h2>

      {items === null ? (
        <div className="py-20 text-center"><Loader2 className="w-7 h-7 text-[#2C2A29] animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <p className="font-decree text-xl text-[#4A4744] mt-6" data-testid="admin-empty">No feedback yet.</p>
      ) : (
        <ul className="mt-6 space-y-4">
          {items.map((f) => (
            <li key={f.id} data-testid={`feedback-row-${f.id}`} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="inline-block text-xs uppercase tracking-wide px-2.5 py-1 border border-[#D9D4CC] rounded-full text-[#7A756E]">{f.category}</span>
                  <p className="font-body text-base text-[#2C2A29] mt-3 whitespace-pre-wrap">{f.message}</p>
                  <p className="font-body text-sm text-[#7A756E] mt-3">
                    {f.name || "Anonymous"}{f.email ? ` · ${f.email}` : ""} · {new Date(f.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  data-testid={`delete-feedback-${f.id}`}
                  onClick={() => remove(f.id)}
                  aria-label="Delete feedback"
                  className="shrink-0 text-[#B7AE9F] hover:text-[#9B3B3B] transition-colors duration-300 p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
